import { services } from '@cwos/api-client';
import { Icon, Origin, Pill } from '@cwos/ui-admin';
import { t } from '@cwos/ui-shared';
import { useNav } from '../navigation.js';
import { formatClock, formatDay } from '../time/timeLogic.js';
import { visitStatusLabel } from '../visits/visitLogic.js';
import {
  CALENDAR_STATE_TONE,
  CONFLICT_DETAIL,
  GROUP_CODES,
  REASON_CODE_LABELS,
  type CalendarItem,
} from './scheduleBoard.js';

/**
 * THE VISIT DRAWER — read-only by owner ruling. An EHR occurrence is owned by
 * the sync; Schedule shows it and Visits is where Phibian records against it,
 * so the only action here is the door to that visit on Visits.
 *
 * Every fact is one the roster row already carries — the canonical state the
 * calendar coloured the chip with, the EHR's own lifecycle and status word,
 * provenance, and the reason codes that make it unclean. Nothing is re-read.
 */
export function VisitDrawer({
  item,
  clientLabel,
  staffNames,
  stateLabel,
  connectorName,
  onClose,
}: {
  item: Extract<CalendarItem, { kind: 'visit' }>;
  clientLabel: string;
  /** Names of the credited staff, already resolved; empty when nobody is. */
  staffNames: string[];
  stateLabel: string;
  connectorName: string | null;
  onClose: () => void;
}) {
  const navigate = useNav();
  const v = item.visit;
  const lastSynced = v.lastSyncedAt
    ? `${formatDay(v.lastSyncedAt)}, ${formatClock(v.lastSyncedAt)}`
    : null;
  return (
    <aside className="drawer" aria-label={`Visit details for ${clientLabel}`}>
      <div className="drawer-head">
        <div>
          <h2 className="rn-card-title">{clientLabel}</h2>
          <p className="rn-muted" style={{ margin: 0 }}>
            {formatDay(v.startsAt)} · {formatClock(v.startsAt)}
            {v.endsAt ? ` – ${formatClock(v.endsAt)}` : ''}
          </p>
        </div>
        <button type="button" className="drawer-close" aria-label="Close" onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>

      <section className="drawer-section">
        <h3 className="drawer-label">Visit</h3>
        <div className="drawer-rows">
          <div className="drawer-row">
            <span className="rn-muted">Status</span>
            <Pill variant={CALENDAR_STATE_TONE[item.state]} dot>
              {stateLabel}
            </Pill>
          </div>
          {item.detail ? (
            <div className="drawer-row">
              <span className="rn-muted">Detail</span>
              <span>{item.detail}</span>
            </div>
          ) : null}
          <div className="drawer-row">
            <span className="rn-muted">Staff</span>
            <span>{staffNames.length > 0 ? staffNames.join(', ') : 'Nobody staffed'}</span>
          </div>
          {v.occurrenceState ? (
            <div className="drawer-row">
              <span className="rn-muted">EHR lifecycle</span>
              <span>{services.OCCURRENCE_STATE_LABELS[v.occurrenceState]}</span>
            </div>
          ) : null}
          {v.externalStatus ? (
            <div className="drawer-row">
              <span className="rn-muted">EHR status</span>
              {/* Verbatim, as a quotation (services.ts: never folded into `status`). */}
              <span>“{v.externalStatus}”</span>
            </div>
          ) : null}
          <div className="drawer-row">
            <span className="rn-muted">In Phibian</span>
            <span>{visitStatusLabel(v.status)}</span>
          </div>
          {v.minutesAttended !== null ? (
            <div className="drawer-row">
              <span className="rn-muted">Minutes attended</span>
              <span>{v.minutesAttended}</span>
            </div>
          ) : null}
          {v.source === 'imported' ? (
            <div className="drawer-row">
              <span className="rn-muted">Origin</span>
              <Origin
                source={v.source}
                connectorName={connectorName}
                vendorAbsentSince={v.vendorAbsentSince}
                lastSyncedAt={lastSynced}
              />
            </div>
          ) : null}
        </div>
      </section>

      {item.unresolved ? (
        <section className="drawer-section">
          <h3 className="drawer-label">Needs a look</h3>
          <ul className="drawer-rows" style={{ margin: 0, paddingLeft: 'var(--space-16)' }}>
            {item.workerIds.length === 0 ? <li>No staff member resolves to anyone here</li> : null}
            {v.sourceStatusConflict ? <li>{CONFLICT_DETAIL}</li> : null}
            {/* The same reasons `toCalendarItems` flags on — a group visit is not one. */}
            {v.reasonCodes
              .filter((code) => !GROUP_CODES.has(code))
              .map((code) => (
                <li key={code}>{REASON_CODE_LABELS[code]}</li>
              ))}
          </ul>
        </section>
      ) : null}

      <section className="drawer-section">
        {v.source === 'imported' ? (
          <p className="rn-muted" style={{ marginTop: 0 }}>
            {t('schedule.visitReadOnly')}
          </p>
        ) : null}
        <div className="drawer-doors">
          <button
            type="button"
            className="rn-link"
            onClick={() => navigate(`/visits?occurrence=${encodeURIComponent(v.id)}`)}
          >
            Open in Visits →
          </button>
        </div>
      </section>
    </aside>
  );
}
